import { authenticatedInstance } from '../index/index.service';
import type { AccountantReportView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { AccountantReportView, AccountantReportRow, AccountantReportTotals } from 'shared-models/browser';

class AccountantReportService {
  // Summary: דו"ח חשבונאי — date-range mode.
  async getByRange(tenantId: string, from: string, to: string, employeeSession: string): Promise<AccountantReportView> {
    try {
      const response = await authenticatedInstance.get<AccountantReportView>(
        `/accountant-report/tenant/${encodeURIComponent(tenantId)}/range`,
        { params: { from, to }, headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: דו"ח חשבונאי — specific-sessions mode.
  async getBySessions(tenantId: string, sessionIds: number[], employeeSession: string): Promise<AccountantReportView> {
    try {
      const response = await authenticatedInstance.post<AccountantReportView>(
        `/accountant-report/tenant/${encodeURIComponent(tenantId)}/sessions`,
        { sessionIds },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: xlsx blob of the date-range report.
  async exportRangeXlsx(tenantId: string, from: string, to: string, employeeSession: string): Promise<Blob> {
    try {
      const response = await authenticatedInstance.get(
        `/accountant-report/tenant/${encodeURIComponent(tenantId)}/range/xlsx`,
        { params: { from, to }, headers: { 'x-employee-session': employeeSession }, responseType: 'blob' },
      );
      return response.data as Blob;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: xlsx blob of the specific-sessions report.
  async exportSessionsXlsx(tenantId: string, sessionIds: number[], employeeSession: string): Promise<Blob> {
    try {
      const response = await authenticatedInstance.post(
        `/accountant-report/tenant/${encodeURIComponent(tenantId)}/sessions/xlsx`,
        { sessionIds },
        { headers: { 'x-employee-session': employeeSession }, responseType: 'blob' },
      );
      return response.data as Blob;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const accountantReportService = new AccountantReportService();
